import React, { useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Countdown from "react-countdown";
import Decimal from "decimal.js-light";
import useWindowSize from "../../../hooks/useWindowSize";
import Skeleton from "react-loading-skeleton";
import ThousandsSeparator from "../../../utils/thousands-separator";
import { roundFloat } from "../../../utils/round-float";
import { isMobile } from "react-device-detect";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { getTroopStateLabel } from "./constants";
import { TroopBoxProps } from "./types";
import { CustomIcon } from "../../Icons";
import CalculateTroopAbility from "./CalculateTroopAbility";
import ItemsList from "./ItemsList";
import AlteredLabel from "../../AlteredLabel";
import ModifiedStat from "../../ModifiedStat";
import { alteredThemeOf, healthPair } from "../../../utils/statModifiers";

const LargeView: React.FC<TroopBoxProps> = (props) => {
  const ref = useRef<any>();
  const windowSize = useWindowSize();
  const { t } = useAppTranslation(["barracks"]);
  const getWidth = () => ref?.current?.offsetWidth;

  const abilities = useMemo(() => {
    return props?.abilities?.filter((ability) => ability?.Value > 0);
  }, [props?.abilities]);
  // Set while an event modifier with a label changes this troop.
  const alteredTheme = alteredThemeOf(props);

  return (
    <div>
      <div ref={ref} className={"land-box py-4 text-light " + props.className}>
        {props.level && (
          <button className="level-btn brk-step4 fw-bold text-light mx-2">
            {props.level}
          </button>
        )}
        <div className="lb-body px-2 mb-3">
          <div
            ref={ref}
            className={"text-center mb-4 " + (!props.loading && "d-none")}
          >
            <span>
              <Skeleton
                width={getWidth() - 75}
                height={getWidth() - 75}
                borderRadius={15}
                circle={windowSize.width >= 1400 ? true : false}
              />
            </span>
          </div>
          {!props.loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              style={{ position: "relative" }}
              className="image px-3  text-center"
            >
              {!props.train && props.xp?.toString() ? (
                <div style={{ fontSize: 14 }} className="xp-badge brk-step5">
                  {t("barracks:labels.xp")}: {props.xp}/{props.maxXp}
                </div>
              ) : (
                ""
              )}
              <div
                style={{ width: "100%", height: 261 }}
                className="d-flex align-items-center justify-content-center position-relative"
              >
                {props.leverage && (
                  <div className="p-absolute leverage">{props.leverage}X</div>
                )}
                {/* Left item slots (3) */}
                <div
                  className="d-flex flex-column align-items-center position-absolute"
                  style={{
                    left: 0,
                    top: 10,
                    bottom: 10,
                    justifyContent: "center",
                  }}
                >
                  <ItemsList
                    size={30}
                    variant="slots"
                    direction="column"
                    slotCount={3}
                    startIndex={0}
                    items={props.items as any}
                    setBonuses={(props as any).setBonuses}
                  />
                </div>
                <img
                  style={{
                    marginBottom: props.nft ? 20 : 0,
                    maxWidth: "85%",
                    maxHeight: "85%",
                  }}
                  src={props?.image || props?.troop?.image}
                  alt={props?.name || props?.troop?.name}
                  className=""
                />
                {alteredTheme && (
                  <AlteredLabel
                    theme={alteredTheme}
                    kind="troop"
                    className="altered-label--above-title"
                  />
                )}
                {/* Right item slots (3) */}
                <div
                  className="d-flex flex-column align-items-center position-absolute"
                  style={{
                    right: 0,
                    top: 10,
                    bottom: 10,
                    justifyContent: "center",
                  }}
                >
                  <ItemsList
                    size={30}
                    variant="slots"
                    direction="column"
                    slotCount={3}
                    startIndex={3}
                    items={props.items as any}
                    setBonuses={(props as any).setBonuses}
                  />
                </div>
                <div
                  className="abilities justify-content-between w-100"
                  style={alteredTheme ? { bottom: 60 } : undefined}
                >
                  {abilities &&
                    abilities.length > 0 &&
                    abilities.map((ability, index) => (
                      <OverlayTrigger
                        key={index}
                        placement="bottom"
                        flip
                        overlay={
                          <Tooltip className="fs-tiny" {...(props as any)}>
                            <CalculateTroopAbility
                              isMarket={props?.isMarket}
                              bonus={props?.bonus}
                              ability={ability}
                              isPercentage={
                                props?.troop?.abilities?.[index]?.ValueType ===
                                "Percentage"
                              }
                            />
                          </Tooltip>
                        }
                        delay={{ show: 100, hide: 200 }}
                      >
                        <div style={{ marginTop: 5 }}>
                          {ability?.Icon && (
                            <img alt="" src={ability?.Icon} width={23} />
                          )}
                        </div>
                      </OverlayTrigger>
                    ))}
                </div>
              </div>
            </motion.div>
          )}
          <div className="lb-title">
            <h3 className="h5 mb-0">
              {props.loading ? (
                <Skeleton width={"30%"} borderRadius={10} height={20} />
              ) : (
                props?.name || props?.troop?.name
              )}{" "}
            </h3>
            <div className="d-flex align-items-center justify-content-between">
              {props.loading ? (
                <>
                  <Skeleton width={100} borderRadius={10} height={15} />
                  <Skeleton width={30} borderRadius={10} height={20} />
                </>
              ) : (
                <>
                  <h5 className="fs-med text-success-light m-0">
                    {props.isLearn && (
                      <>
                        {t("barracks:stats.health")}:{" "}
                        {ThousandsSeparator(props.health)}
                      </>
                    )}
                    {props.sellCard
                      ? t("barracks:states.clickForDetails")
                      : props.train
                      ? t("barracks:states.available")
                      : getTroopStateLabel(props?.state, t)}
                  </h5>
                  <div>
                    {!props.isLearn && props?.health?.toString() && (
                      <div
                        style={{
                          borderRadius: 15,
                          textAlign: "center",
                          marginTop: -18,
                        }}
                        className="fs-tiny bg-gray p-relative overflow-hidden percent-badge"
                      >
                        <div
                          style={{
                            width:
                              (props?.health * 100) / props?.maxHealth + "%",
                            position: "absolute",
                            height: "100%",
                            left: 0,
                            backgroundColor: "var(--theme-color-green-dark)",
                            top: 0,
                          }}
                        />
                        <span style={{ zIndex: 10, position: "relative" }}>
                          {t("barracks:stats.health")}:{" "}
                          <ModifiedStat
                            {...healthPair(
                              props.statModifiers,
                              props?.health,
                              props?.maxHealth,
                              (value) => ThousandsSeparator(roundFloat(value)),
                              (value) => ThousandsSeparator(value)
                            )}
                          />
                        </span>
                      </div>
                    )}
                    {props.hourlyHealing ? (
                      <div className="fs-tiny text-end ms-auto mt-1 ">
                        <span style={{ color: "#44c97a" }}>
                          +{props?.hourlyHealing}/{t("barracks:stats.hour")}
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                    {!props.hourlyHealing && props.healthBonus ? (
                      <>
                        <div className="fs-tiny text-end mt-1 ">
                          <CustomIcon
                            icon="icon-health-restore"
                            className="me-1"
                            width={18}
                            height={20}
                          />
                          <span style={{ color: "#44c97a" }}>
                            +{" "}
                            {ThousandsSeparator(
                              roundFloat(props?.healthBonus, 1, 1)
                            )}{" "}
                            {t("barracks:stats.health")}
                          </span>
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                  {props.train && (
                    <span className="fs-tiny bg-gray p-relative overflow-hidden percent-badge">
                      <div
                        style={{
                          width: "100%",
                          position: "absolute",
                          height: "100%",
                          left: 0,
                          backgroundColor: "var(--theme-color-green-dark)",
                          top: 0,
                        }}
                      />
                      <span style={{ zIndex: 10, position: "relative" }}>
                        {t("barracks:stats.health")}: {props?.maxHealth}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          {props.loading ? (
            <div className="lb-list fs-med px-4 my-3">
              <div className="lb-list-item mb-1">
                <div>
                  <Skeleton width={70} borderRadius={5} height={18} />
                </div>
                <div className="fs-med">
                  {" "}
                  <Skeleton width={40} borderRadius={5} height={18} />
                </div>
              </div>
              <div className="lb-list-item mb-1">
                <div>
                  <Skeleton width={70} borderRadius={5} height={18} />
                </div>
                <div className="fs-med">
                  {" "}
                  <Skeleton width={40} borderRadius={5} height={18} />
                </div>
              </div>
              <div className="lb-list-item mb-1">
                <div>
                  <Skeleton width={70} borderRadius={5} height={18} />
                </div>
                <div className="fs-med">
                  <Skeleton width={40} borderRadius={5} height={18} />
                </div>
              </div>
            </div>
          ) : (
            (!props.train || !isMobile) && (
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    fontSize: props.train ? 12 : 15,
                    filter:
                      props.state === 3 ||
                      props.state === 2 ||
                      props.state === 6
                        ? "blur(5px)"
                        : "none",
                  }}
                  className="lb-list brk-step6 fs-med px-4 my-3"
                >
                  <div className="lb-list-item mb-1">
                    <div>
                      <img
                        alt="damage"
                        style={{ width: 25, height: 28 }}
                        className="me-1"
                        src="/assets/images/icons/damage.png"
                      />
                      {t("barracks:stats.damage")}
                    </div>
                    <div className="fs-med">{props.damage}</div>
                  </div>
                  <div className="lb-list-item mb-1">
                    <div>
                      <img
                        alt="armor"
                        style={{ width: 25, height: 28 }}
                        className="me-1"
                        src="/assets/images/icons/armor.png"
                      />
                      {t("barracks:stats.armor")}
                    </div>
                    <div className="fs-med">{props.armor}</div>
                  </div>
                  <div className="lb-list-item mb-1">
                    <div>
                      <img
                        alt="magic"
                        style={{ width: 25, height: 28 }}
                        className="me-1"
                        src="https://core.chainoflegends.com/assets/images/team-magic-resist.png"
                      />
                      {t("barracks:stats.magicResistance")}
                    </div>
                    <div className="fs-med">{props.magicResistance}</div>
                  </div>
                  <div className="lb-list-item mb-1">
                    <div>
                      <img
                        alt="range"
                        style={{ width: 25, height: 28 }}
                        className="me-1"
                        src="/assets/images/icons/range.png"
                      />
                      {t("barracks:stats.range")}
                    </div>
                    <div className="fs-med">{props.range}</div>
                  </div>
                  {props.upgrade && (
                    <div className="lb-list-item mb-1">
                      <div>{t("barracks:stats.health")}</div>
                      <div className="fs-med d-flex align-items-center">
                        {props.maxHealth}
                        <img
                          alt="right"
                          src="/assets/images/icons/arrow-right.svg"
                          className="mx-1"
                          style={{ width: 15 }}
                        />
                        <span className="color-blue bold">
                          {new Decimal(props.maxHealth + props.maxHealth / 2)
                            .toDecimalPlaces(1)
                            .toString()}
                        </span>
                      </div>
                    </div>
                  )}
                  {props.ask || props.bid ? (
                    <div
                      style={{
                        marginBottom: -30,
                        borderTop: "1px solid rgba(255,255,255,0.15)",
                      }}
                      className="lb-list-item fs-small mt-3 pt-3"
                    >
                      <div>
                        {t("barracks:stats.buyerPrice")}:{" "}
                        <span className="text-success bold">
                          {ThousandsSeparator(props.bid?.toString() || "0") ||
                            0}
                        </span>
                      </div>
                      <div>
                        {t("barracks:stats.sellerPrice")}:{" "}
                        <span className="text-danger bold">
                          {ThousandsSeparator(props.ask?.toString() || "0")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                {props?.state === 6 && (
                  <div className="fs-med overlay px-3 px-md-2 px-lg-3 mb-2">
                    <h5 className="text-center mt-3">
                      {t("barracks:states.forSale")}
                    </h5>
                    <h6 className="bold dark-yellow ">
                      {t("barracks:stats.price")}:{" "}
                      {ThousandsSeparator(props.marketOrderPrice)}{" "}
                      <img
                        alt="cleg"
                        src="/assets/images/icons/cleg.png"
                        width={22}
                        height={22}
                      />{" "}
                      {t("barracks:stats.cleg")}
                    </h6>
                  </div>
                )}
                {(props.state === 3 || props.state === 2) && (
                  <div className="fs-med overlay px-3 px-md-2 px-lg-3 mb-2 ">
                    <h5 className="text-center">
                      {props.state === 3
                        ? t("barracks:states.upgrading")
                        : t("barracks:states.training")}
                    </h5>
                    <div>
                      <Countdown
                        date={new Date(props.finishUpgradeTime as string)}
                        onComplete={() =>
                          setTimeout(() => {
                            props.refresh && props.refresh();
                          }, 6000)
                        }
                        renderer={({ formatted }) => (
                          <div
                            style={{ transform: "scale(0.8)" }}
                            className="fs-big justify-content-center d-flex"
                          >
                            {parseInt(formatted.days) ? (
                              <div className="time-badge">
                                {formatted.days}
                                <span className="dark-yellow">
                                  {t("barracks:timeUnits.days")}
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                            <div className="ms-2 time-badge">
                              {formatted.hours}
                              <span className="dark-yellow">
                                {t("barracks:timeUnits.hours")}
                              </span>
                            </div>
                            <div className="ms-2 time-badge">
                              {formatted.minutes}
                              <span className="dark-yellow">
                                {t("barracks:timeUnits.minutes")}
                              </span>
                            </div>
                            <div className="ms-2 time-badge">
                              {formatted.seconds}
                              <span className="dark-yellow">
                                {t("barracks:timeUnits.seconds")}
                              </span>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
        {props.upgrade && (
          <div className="fs-tiny text-center dark-yellow">
            {t("barracks:stats.estimatedUpgradeTime")}:{" "}
            <b>{props.upgradeMinutes}</b> {t("barracks:stats.minutes")}
          </div>
        )}
        {(props.train || props.isLearn) && (
          <div style={props.isLearn ? { marginBottom: -40 } : undefined}>
            {props.clegCost &&
            +props.clegCost?.toString()?.replaceAll(",", "") ? (
              <div className="d-flex flex-wrap  justify-content-center fs-small mt-3 ">
                <div className="">{t("barracks:stats.cost")}:</div>
                {props?.clegCost &&
                (!props?.lastClegCost ||
                  props?.clegCost !== props?.lastClegCost) ? (
                  <div className="d-flex ms-2 align-items-center">
                    <img
                      alt="cleg"
                      src={"/assets/images/icons/cleg.png"}
                      width={25}
                      height={25}
                    />
                    {props?.clegCost}
                  </div>
                ) : props?.clegCost ? (
                  <div className="d-flex ms-2 align-items-end">
                    <img
                      alt="cleg"
                      src={"/assets/images/icons/cleg.png"}
                      width={25}
                      height={25}
                    />
                    <div className="d-flex align-items-center flex-column">
                      <del className="text-tiny">{props?.lastClegCost}</del>
                      <div className="text-success-light bold">
                        {props?.clegCost}
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {props?.stoneCost &&
                (!props?.lastStoneCost ||
                  props?.stoneCost !== props?.lastStoneCost) ? (
                  <div className="d-flex ms-3 align-items-center">
                    <img
                      alt="stone"
                      src={"/assets/images/icons/stone.png"}
                      width={25}
                      height={25}
                    />
                    {props?.stoneCost}
                  </div>
                ) : props?.stoneCost ? (
                  <div className="d-flex ms-2 align-items-end">
                    <img
                      alt="stone"
                      src={"/assets/images/icons/stone.png"}
                      width={25}
                      height={25}
                    />
                    <div className="d-flex align-items-center flex-column">
                      <del className="text-tiny">{props?.lastStoneCost}</del>
                      <div className="text-success-light bold">
                        {props?.stoneCost}
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {props?.ironCost &&
                (!props?.lastIronCost ||
                  props?.ironCost !== props?.lastIronCost) ? (
                  <div className="d-flex ms-3 align-items-center">
                    <img
                      alt="metal"
                      src={"/assets/images/icons/metal.png"}
                      width={25}
                      height={25}
                    />
                    {props?.ironCost}
                  </div>
                ) : props?.ironCost ? (
                  <div className="d-flex ms-2 align-items-end">
                    <img
                      alt="metal"
                      src={"/assets/images/icons/metal.png"}
                      width={25}
                      height={25}
                    />
                    <div className="d-flex align-items-center flex-column">
                      <del className="text-tiny">{props?.lastIronCost}</del>
                      <div className="text-success-light bold">
                        {props?.ironCost}
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>
            ) : (
              ""
            )}
          </div>
        )}
        <div className="lb-button text-center px-2 d-flex justify-content-center">
          {props.loading ? (
            <div className="mt-1" style={{ marginBottom: -50 }}>
              <Skeleton width={"90%"} borderRadius={10} height={50} />
            </div>
          ) : (
            props.action
          )}
        </div>
      </div>
    </div>
  );
};

export default LargeView;
